import Button from 'tio-ui/components/button';
import Divider from 'tio-ui/components/divider';
import { eq } from 'tio-ui/utilities';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { Section } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import inc from '../../../helpers/inc.ts';
import ApproverQueueEmployeeInfo from './approver-queue-employee-info.gts';
import Component from '@glimmer/component';
import ReassignModal from '../approval-queue/reassign-modal.gts';
import Table from 'tio-ui/components/table';
import type {
  ApplicationApprovalSummary,
  ApplicationApprovalSummaryApprover,
} from '../../../types/tuition-assistance';
import type FeaturesService from '../../../services/features.ts';
import type StoreService from '@ember-data/store';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type TasProgramInstance from 'tio-data/models/tas-program-instance';

interface TuitionAssistanceApplicationDetailsApprovalQueueStatusSignature {
  Args: {
    application: TasApplicationModel | TasProgramInstance;
    consumingApp: string;
    isReadonlyView: boolean;
  };
}

export default class TuitionAssistanceApplicationDetailsApprovalQueueStatusComponent extends Component<TuitionAssistanceApplicationDetailsApprovalQueueStatusSignature> {
  @tracked isOpenReassignModal = false;
  @tracked selectedReviewerIndex: number | null = null;
  @tracked moreTriggerElement: HTMLElement | null = null;
  @tracked approverToReassign: ApplicationApprovalSummaryApprover | null = null;

  @service declare store: StoreService;
  @service declare features: FeaturesService;

  get affirmedReviewers() {
    return this.approvalQueue.affirmed;
  }

  get pendingReviewers() {
    return this.setPendingReviewers(this.approvalQueue);
  }

  get approvalQueue(): ApplicationApprovalSummary {
    const { application } = this.args;

    switch (application.state) {
      case 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL':
        return application.evidenceApproval;
      case 'TAS.ApplicationState.PENDING_COURSES_APPROVAL':
        return application.coursesApproval;
      case 'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL':
        return application.programApproval;
      case 'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL':
        return application.completionApproval;
      case 'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL':
        return application.withdrawalApproval;
      default:
        return {
          affirmed: [],
          current_reviewers: [],
          possible_reviewers: [],
          required: [],
          approved: false,
        };
    }
  }

  groupReviewersByOrdinal(reviewers: ApplicationApprovalSummaryApprover[]) {
    const minOrdinal = Math.min(...reviewers.map((reviewer) => reviewer.ordinal));

    const groupedReviewers = reviewers.reduce<Record<number, ApplicationApprovalSummaryApprover[]>>(
      (result, reviewer) => {
        const { ordinal } = reviewer;

        if (!(ordinal - minOrdinal in result)) {
          result[ordinal - minOrdinal] = [];
        }
        result[ordinal - minOrdinal]?.push(reviewer);

        return result;
      },
      {}
    );

    return groupedReviewers;
  }

  setPendingReviewers(approvalQueue: ApplicationApprovalSummary) {
    const { affirmed, possible_reviewers } = approvalQueue;

    let tioApproverFound = false;

    const pendingReviewersQueue = possible_reviewers.filter((reviewer) => {
      const hasAlreadyApproved = affirmed.some(
        (approved) => approved.employee_id === reviewer.employee_id
      );
      const hasMatchingOrdinal = affirmed.some((approved) => approved.ordinal === reviewer.ordinal);

      if (reviewer.dot === 'TAS.Approver.TIO_APPROVER') {
        if (tioApproverFound) {
          // Skips all other TIO_APPROVERs
          return false;
        } else {
          tioApproverFound = true;
        }
      }
      return !hasAlreadyApproved && !hasMatchingOrdinal;
    });

    return this.groupReviewersByOrdinal(pendingReviewersQueue);
  }

  get approvalQueueHasLength() {
    return !!this.approvalQueue.possible_reviewers.length;
  }

  get affirmedApproverHasLength() {
    return !!this.affirmedReviewers.length;
  }

  get companyId(): string {
    const application = this.args.application;
    const id =
      application.modelName === 'tas-program-instance'
        ? application.employee.company.id
        : application.tasProgramInstance.employee.company.id;
    return id ?? '';
  }

  onMoreClick = (reviewer: ApplicationApprovalSummaryApprover, event: Event) => {
    this.moreTriggerElement = event.target as HTMLElement;
    this.approverToReassign = reviewer;
    this.isOpenReassignModal = !this.isOpenReassignModal;
  };

  onCloseReassignModal = () => {
    this.approverToReassign = null;
    this.isOpenReassignModal = !this.isOpenReassignModal;
  };

  <template>
    {{#if this.approvalQueueHasLength}}
      <Section>
        <:header>
          {{t "tuition_assistance.application_details.approval_queue_status"}}
        </:header>
        <:body>
          {{#if this.affirmedApproverHasLength}}
            <h4 class="font-semibold leading-loose tio-fg-secondary my-1">
              {{t "tuition_assistance.application_details.who_has_approved"}}
            </h4>
            <Table @isLoading={{false}}>
              <:thead as |options|>
                <options.tr>
                  <options.th>
                    {{t "tuition_assistance.application_details.employee_id"}}
                  </options.th>
                  <options.th>
                    {{t "tuition_assistance.application_details.role"}}
                  </options.th>
                </options.tr>
              </:thead>
              <:tbody as |options|>
                {{#each this.affirmedReviewers as |approver|}}
                  <ApproverQueueEmployeeInfo
                    @cellClass={{options.cellClass}}
                    @reviewer={{approver}}
                    @consumingApp={{@consumingApp}}
                  />
                {{/each}}
              </:tbody>
            </Table>
            <Divider class="my-2" />

          {{/if}}
          <h4 class="col-span-2 font-semibold leading-loose tio-fg-secondary my-1">
            {{t "tuition_assistance.application_details.who_is_next"}}
          </h4>
          <Table @isLoading={{false}}>
            <:thead as |options|>
              <options.tr>
                <options.th />
                <options.th>
                  {{t "tuition_assistance.application_details.employee_id"}}
                </options.th>
                <options.th>
                  {{t "tuition_assistance.application_details.role"}}
                </options.th>
                <options.th />
              </options.tr>
            </:thead>
            <:tbody as |options|>
              {{#each-in this.pendingReviewers as |index reviewerSet|}}
                {{#each reviewerSet as |reviewer reviewerIndex|}}
                  <options.tr>
                    <options.td>
                      {{#unless (eq reviewerIndex "0")}}
                        <div
                          class="tio-inline-row-center col-span-1 w-6 h-6 rounded-full border tio-border-info tio-fg-info"
                        >{{inc index}}</div>
                      {{/unless}}
                    </options.td>
                    <ApproverQueueEmployeeInfo
                      @cellClass={{options.cellClass}}
                      @reviewer={{reviewer}}
                      @consumingApp={{@consumingApp}}
                    />
                    <options.td class="text-center">
                      <Button @appearance="minimal" {{on "click" (fn this.onMoreClick reviewer)}}>
                        {{t "tuition_assistance.approval_queue.reassign"}}
                      </Button>
                    </options.td>
                  </options.tr>
                {{/each}}
              {{/each-in}}
            </:tbody>
          </Table>
          {{#if this.approverToReassign}}
            <ReassignModal
              @show={{this.isOpenReassignModal}}
              @onClose={{this.onCloseReassignModal}}
              @approverToReassign={{this.approverToReassign}}
              @application={{@application}}
              @companyId={{this.companyId}}
            />
          {{/if}}
        </:body>
      </Section>
    {{/if}}
  </template>
}
